// ── API Client ────────────────────────────────────────────────
// Fetch wrapper with JWT access token management and silent refresh.

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// API base prefix. Empty for a root-mounted, same-origin deployment (local docker / staging),
// where the SPA's own nginx proxies /internal/v1 → the api service. In the internal-apps
// forward-auth deployment the SPA lives under a subpath and the platform nginx routes
// /address-validation/api/* → the api container, so the build sets VITE_API_BASE=/address-validation/api.
const API_BASE = import.meta.env.VITE_API_BASE ?? '';
export const API_PREFIX = `${API_BASE}/internal/v1`;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Le corps JSON du refus, quand il y en a un. Certains refus disent PLUS que leur message — un `code`, le
     *  calcul de capacité sur lequel ils portent — et l'écran s'en sert pour demander ce qui manque au lieu
     *  d'afficher une phrase (C0.5). */
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_PREFIX}${path}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  // Let the browser set Content-Type for FormData (with multipart boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // send cookies (refresh token)
  });

  // If 401 and we had a token, try silent refresh then retry once
  if (res.status === 401 && accessToken) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, body.message || res.statusText, body);
  }

  return res.json() as Promise<T>;
}

/**
 * Authenticated fetch that returns the raw Blob — for binary downloads (attachment files/images), where an
 * <img src> / <a href> can't carry the JWT header. Mirrors apiFetch's auth + one silent-refresh retry.
 */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const url = `${API_PREFIX}${path}`;
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  let res = await fetch(url, { headers, credentials: 'include' });
  if (res.status === 401 && accessToken) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(url, { headers, credentials: 'include' });
    }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, body.message || res.statusText);
  }
  return res.blob();
}

/**
 * Authenticated raw-body file upload: the file IS the request body (application/octet-stream), with the
 * name/mime carried by the caller in the query string — the API forwards the bytes to Uniticket as
 * multipart. Bypasses apiFetch because that helper force-sets a JSON Content-Type on non-FormData
 * bodies. Same auth + one silent-refresh retry as apiFetchBlob.
 */
export async function apiUploadFile<T>(path: string, file: File | Blob): Promise<T> {
  const url = `${API_PREFIX}${path}`;
  const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  let res = await fetch(url, { method: 'POST', headers, body: file, credentials: 'include' });
  if (res.status === 401 && accessToken) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(url, { method: 'POST', headers, body: file, credentials: 'include' });
    }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, body.message || res.statusText);
  }
  return res.json() as Promise<T>;
}

async function silentRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!res.ok) {
      accessToken = null;
      return false;
    }

    const data = await res.json();
    accessToken = data.accessToken;
    return true;
  } catch {
    accessToken = null;
    return false;
  }
}
